import { useEffect, useState } from 'react'
import { gql, useApolloClient } from '@apollo/client'
import { loadConnectAndInitialize } from '@stripe/connect-js'
import { createStripeAccountSession } from '../../apollo/mutations'
import { connectAppearance, connectFonts } from './tokens'

const CREATE_SESSION = gql`${createStripeAccountSession}`

/**
 * Stripe's embedded components run on an Account Session the backend creates
 * for this restaurant. The first session also carries the publishable key, so
 * it is fetched before Connect.js is initialised; later calls (Stripe asks
 * again whenever a session expires) mint a fresh one.
 */
export default function useStripeConnect(restaurantId, enabled) {
  const client = useApolloClient()
  const [connect, setConnect] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!enabled || !restaurantId) return undefined
    let cancelled = false
    const createSession = async () => {
      const res = await client.mutate({ mutation: CREATE_SESSION, variables: { restaurantId } })
      return res.data.createStripeAccountSession
    }
    createSession()
      .then((first) => {
        if (cancelled) return
        let unused = first.clientSecret
        setConnect(
          loadConnectAndInitialize({
            publishableKey: first.publishableKey,
            fetchClientSecret: async () => {
              if (unused) {
                const secret = unused
                unused = null
                return secret
              }
              return (await createSession()).clientSecret
            },
            fonts: connectFonts,
            appearance: connectAppearance,
          }),
        )
      })
      .catch((e) => {
        if (!cancelled) setError(e.message)
      })
    return () => {
      cancelled = true
    }
  }, [client, restaurantId, enabled])

  return { connect, error }
}
